import React, { Fragment, MouseEventHandler, useCallback, useContext, useEffect, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { virtualize } from 'react-swipeable-views-utils';
import { HeaderContext } from 'contexts/HeaderContext';
import { CategoryContext } from 'contexts/CategoryContext';
import { curationType, MedicalCategory } from 'model/graphql';
import router from 'next/router';
import { MedicalServiceProvider } from 'contexts/MedicalServiceContext';
import { LocationContext } from 'contexts/LocationContext';

import { AnalyticsContext } from 'contexts/AnalyticsContext';
import MedicalServiceView from '../MedicalServiceView';
import * as styles from './style';

const VirtualizedSwipeableViews = virtualize(SwipeableViews);

// @ts-ignore
function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

export type OnCataegoryChangedCallback = (category: MedicalCategory, index: number) => void;

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
};

export default function MedicalCategoryTab() {
  const { selectedCuration, topLevelCategories, curationLocations } = useContext(CategoryContext);
  const { setTitle } = useContext(HeaderContext);
  const { coordinate } = useContext(LocationContext);
  const { trackEvent } = useContext(AnalyticsContext);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isCategoryExpanded, setIsCategoryExpanded] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isInitialRouterUsed, setInitialRouterUsed] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<MedicalCategory | null>(null);

  const selectedCategory = topLevelCategories[selectedTabIndex];

  useEffect(() => {
    const { lv1: level1 } = router.query;
    if (level1) {
      const foundIndex = topLevelCategories?.findIndex((category) => category?.id === level1);
      if (foundIndex >= 0) {
        setSelectedTabIndex(foundIndex);
      }
    }
  }, [topLevelCategories]);

  useEffect(() => {
    if (selectedCuration?.curationType === curationType.location) {
      setTitle('내 주변');

      const { lo: location } = router.query;
      if (location) {
        const locationTitle =
          curationLocations.find((curationLocation) => curationLocation?.id === location)?.name || '내 주변';
        setTitle(locationTitle);
      }
    } else {
      setTitle(selectedCuration?.name);
    }
  }, [selectedCuration]);

  useEffect(() => {
    if (!topLevelCategories || topLevelCategories?.length === 0 || topLevelCategories?.length < selectedTabIndex) {
      return;
    }
    const categoryId = topLevelCategories[selectedTabIndex]?.id;
    setSelectedCategoryId(categoryId);
  }, [topLevelCategories, selectedTabIndex]);

  useEffect(() => {
    scrollToTop();
  }, [selectedTabIndex]);

  useEffect(() => {
    if (selectedCategory && selectedSubcategory) {
      trackEvent('clinic_productList_view', {
        curationId: selectedCuration?.id,
        curationName: selectedCuration?.name,
        level1CategoryId: selectedCategory?.id,
        level1CategoryName: selectedCategory?.name,
        level2CategoryId: selectedSubcategory?.id,
        level2CategoryName: selectedSubcategory?.name,
        // TODO array support
        // filter_category_id,
        // filter_category_name,
        // filter_id,
        // filter_name,
        lt: coordinate?.latitude,
        lg: coordinate?.longitude,
      });
    }
  }, [
    coordinate?.latitude,
    coordinate?.longitude,
    selectedCategory,
    selectedCuration?.id,
    selectedCuration?.name,
    selectedSubcategory,
  ]);

  const handleSubcategoryUpdate = useCallback((subcategory: MedicalCategory) => {
    setSelectedSubcategory(subcategory);
  }, []);

  const handleSwipeableViewChangeIndex = (index: number) => {
    if (selectedTabIndex === index) return;
    setSelectedTabIndex(index);
  };

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: any) => {
    if (selectedTabIndex === newValue) return;
    setSelectedTabIndex(newValue);
  };

  return (
    <>
      <div className={styles.tabWrapper}>
        <Tabs
          value={selectedTabIndex}
          textColor="inherit"
          onChange={handleTabChange}
          className={styles.tabs}
          variant="scrollable"
          indicatorColor="secondary"
        >
          {topLevelCategories?.map((category: MedicalCategory, index: number) => (
            <Tab
              key={index}
              value={index}
              label={category?.name}
              className={styles.tab(index === selectedTabIndex)}
              {...a11yProps(index)}
              onClick={() =>
                trackEvent('clinic_productList_categoryTab_click', {
                  curationId: selectedCuration?.id,
                  curationName: selectedCuration?.name,
                  level1CategoryId: category.id,
                  level1CategoryName: category.name,
                  status: 'tab',
                })
              }
            />
          ))}
        </Tabs>
        <div className={styles.categoryExpandingWrapper}>
          <CategoryExpandingButton
            isOn={isCategoryExpanded}
            onClick={() => {
              trackEvent('clinic_productList_categoryTabbar_click', {
                level1CategoryId: selectedCategory?.id,
                level1CategoryName: selectedCategory?.name,
                action: isCategoryExpanded ? 'ufold' : 'fold',
              });
              setIsCategoryExpanded(!isCategoryExpanded);
            }}
          />
        </div>
      </div>
      {isCategoryExpanded && (
        <>
          <DimmedBackgroundView onClick={() => setIsCategoryExpanded(!isCategoryExpanded)} />
          <CategoryPanel
            selectedCategoryId={selectedCategoryId}
            categories={topLevelCategories}
            onPanelClick={() => setIsCategoryExpanded(false)}
            onCategoryChanged={(_, index) => {
              const category = topLevelCategories[index];

              if (category) {
                trackEvent('clinic_productList_categoryTab_click', {
                  curationId: selectedCuration?.id,
                  curationName: selectedCuration?.name,
                  level1CategoryId: category.id,
                  level1CategoryName: category.name,
                  status: 'tabbar',
                });
              }

              setSelectedTabIndex(index);
            }}
          />
        </>
      )}
      <VirtualizedSwipeableViews
        axis="x"
        index={selectedTabIndex}
        resistance
        slideCount={topLevelCategories?.length ?? 0}
        onChangeIndex={handleSwipeableViewChangeIndex}
        slideRenderer={({ key, index }) => {
          if (index < 0 || !topLevelCategories) return <Fragment key={key} />;
          const category = topLevelCategories[index];
          if (!category) return <Fragment key={key} />;

          return (
            <MedicalServiceProvider key={key}>
              <MedicalServiceView
                curation={selectedCuration}
                category={category}
                isVisible={index === selectedTabIndex}
                allowFetch={selectedTabIndex === index || index === selectedTabIndex + 1}
                isInitialRouterUsed={isInitialRouterUsed}
                setInitialRouterUsed={setInitialRouterUsed}
                onSubcategoryUpdate={handleSubcategoryUpdate}
              />
            </MedicalServiceProvider>
          );
        }}
      />
    </>
  );
}

function DimmedBackgroundView({ onClick }: { onClick: MouseEventHandler }) {
  return <div className={styles.dimmedBackgroundView} onClick={onClick} />;
}

function CategoryPanel({
  categories,
  selectedCategoryId,
  onPanelClick,
  onCategoryChanged,
}: {
  categories: MedicalCategory[];
  selectedCategoryId: String;
  onPanelClick: MouseEventHandler;
  onCategoryChanged: OnCataegoryChangedCallback;
}) {
  return (
    <ul className={styles.categoryPanel} onClick={onPanelClick}>
      {categories?.map((category: MedicalCategory, index: number) => (
        <li
          key={category.id}
          style={{
            height: '43px',
            marginRight: '8px',
            marginBottom: '8px',
          }}
          className={styles.categoryPanelItem(selectedCategoryId === category.id)}
          onClick={() => {
            onCategoryChanged(category, index);
          }}
        >
          {category.name}
        </li>
      ))}
    </ul>
  );
}

function CategoryExpandingButton({ isOn, onClick }: { isOn: boolean; onClick: MouseEventHandler }) {
  return (
    <svg
      className={styles.categoryExpandingButton(isOn)}
      onClick={onClick}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M16 7L10 13L4 7" stroke="#111723" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
